import { ExternalLink, RotateCw } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { recordsDeliveryPreview } from '@/shared/api/generated/delivery/delivery';
import { apiURL } from '@/shared/config';
import { useDebounced } from '@/shared/lib/useDebounced';
import { Button } from '@/shared/ui/Button';

import { previewAddress } from './previewFocus';
import styles from './RecordPage.module.scss';

export interface PreviewPanelProps {
  recordID: string;
  slug: string | undefined;
  route: string;
  /** Адрес, по которому сайт окружения открывается во фрейме. */
  base: string | undefined;
  values: Record<string, unknown>;
  locale: string | undefined;
  onSelect: (selection: { block?: string; field?: string }) => void;
}

/**
 * Живой предпросмотр: сайт во фрейме рядом с формой.
 *
 * На каждую правку (с паузой, чтобы не слать на каждую букву) сервер
 * собирает несохранённые значения так, как их отдаст API выдачи, и запись
 * уходит во фрейм — сайт с SDK перерисовывается без сохранения и
 * перезагрузки. Щелчок по блоку на сайте открывает его поля в форме.
 */
export function PreviewPanel({
  recordID,
  slug,
  route,
  base,
  values,
  locale,
  onSelect,
}: PreviewPanelProps) {
  const frame = useRef<HTMLIFrameElement>(null);
  const latest = useRef<unknown>(undefined);
  const [reload, setReload] = useState(0);
  const [connected, setConnected] = useState(false);
  const debounced = useDebounced(values, 300);

  const src = previewAddress(base, route, { id: recordID, slug }, window.location.origin);
  const siteOrigin = src === undefined ? undefined : new URL(src).origin;

  const send = (item: unknown) => {
    if (siteOrigin !== undefined && item !== undefined) {
      frame.current?.contentWindow?.postMessage(
        { source: 'faceta', type: 'update', item },
        siteOrigin,
      );
    }
  };

  // Сообщения сайта: готов (отправить ему текущее) и выбрал блок.
  useEffect(() => {
    const listen = (event: MessageEvent) => {
      const data = event.data as {
        source?: string;
        type?: string;
        block?: string;
        field?: string;
      } | null;

      if (
        event.origin !== siteOrigin ||
        event.source !== frame.current?.contentWindow ||
        data?.source !== 'faceta'
      ) {
        return;
      }

      if (data.type === 'ready') {
        setConnected(true);
        send(latest.current);
      }

      if (data.type === 'select') {
        onSelect({
          ...(data.block === undefined ? {} : { block: data.block }),
          ...(data.field === undefined ? {} : { field: data.field }),
        });
      }
    };

    window.addEventListener('message', listen);

    return () => {
      window.removeEventListener('message', listen);
    };
  });

  // Правки формы — во фрейм, собранными сервером.
  useEffect(() => {
    if (src === undefined) {
      return;
    }

    let cancelled = false;

    recordsDeliveryPreview(recordID, {
      data: debounced,
      ...(locale === undefined ? {} : { locale }),
    })
      .then((response) => {
        if (cancelled || response.status !== 200) {
          return;
        }

        latest.current = absolutize(response.data, apiURL());
        send(latest.current);
      })
      .catch(() => {
        // Предпросмотр — подсказка, а не сохранение: сбой не мешает работе.
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced, recordID, locale, src]);

  if (src === undefined) {
    return (
      <aside className={styles['preview']}>
        <h2 className={styles['history__title']}>Предпросмотр</h2>
        <p className={styles['muted']}>
          {base === undefined || base === ''
            ? 'У окружения не указан адрес сайта — задайте его в настройках сайта (у Production — адрес предпросмотра, препрод-сборку).'
            : route === ''
              ? 'У типа не задан адрес записи на сайте — задайте его в «Моделях», например «/blog/{slug}».'
              : 'У записи нет адреса (slug) — без него не понять, какую страницу открыть.'}
        </p>
      </aside>
    );
  }

  return (
    <aside className={`${styles['preview'] ?? ''} ${styles['preview--live'] ?? ''}`}>
      <div className={styles['preview__bar']}>
        <span className={styles['preview__address']} title={src}>
          {new URL(src).pathname}
        </span>
        {!connected && (
          <span className={styles['muted']}>сайт без SDK — обновится после сохранения</span>
        )}
        <Button
          iconOnly
          size="sm"
          variant="ghost"
          tone="neutral"
          aria-label="Обновить страницу"
          onClick={() => {
            setConnected(false);
            setReload((value) => value + 1);
          }}
        >
          <RotateCw size={15} />
        </Button>
        <Button
          asChild
          iconOnly
          size="sm"
          variant="ghost"
          tone="neutral"
          aria-label="Открыть в новой вкладке"
        >
          <a href={src} target="_blank" rel="noreferrer">
            <ExternalLink size={15} />
          </a>
        </Button>
      </div>
      <iframe
        key={reload}
        ref={frame}
        className={styles['preview__frame']}
        src={src}
        title="Предпросмотр на сайте"
      />
    </aside>
  );
}

/** Адреса файлов от корня API → полные: сайт ставит их в <img src>. */
function absolutize(value: unknown, base: string): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => absolutize(item, base));
  }

  if (value === null || typeof value !== 'object') {
    return value;
  }

  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [
      key,
      (key === 'url' || key === 'poster') &&
      typeof item === 'string' &&
      item.startsWith('/v1/files/')
        ? `${base}${item}`
        : absolutize(item, base),
    ]),
  );
}
